import { invenioConfig } from "@config";
import { getUiSchemaExtensions } from "@forms/rjsf/RJSFExtensionFields";
import _merge from "lodash/merge";

// Constants
const BOOLEAN_UI_OPTIONS = {
  "ui:options": {
    semantic: { toggle: true },
  },
};

const ARRAY_UI_OPTIONS = {
  "ui:options": { orderable: false },
};

const TEXTAREA_WIDGET = { "ui:widget": "textarea" };

const VOCABULARY_SEARCH_LANGUAGE = {
  "ui:widget": "vocabularySearch",
  "ui:placeholder": "Type a language...",
  "ui:options": { vocabularyType: "language" },
};

const VOCABULARY_SEARCH_COUNTRY = {
  "ui:widget": "vocabularySearch",
  "ui:placeholder": "Type a country...",
  "ui:options": { vocabularyType: "country" },
};

const HIDDEN_WIDGET = { "ui:widget": "hidden" };

// Utility functions
const createVocabularyWidget = (vocabularyType, useValue = false) => ({
  "ui:widget": "vocabulary",
  "ui:options": { vocabularyType, ...(useValue && { useValue: true }) },
});

const createVocabularySearchWidget = (vocabularyType, placeholder) => ({
  "ui:widget": "vocabularySearch",
  "ui:placeholder": placeholder,
  "ui:options": { vocabularyType },
});

const createIdentifierSchema = (schemeVocabulary, includeMaterial = false) => {
  const baseSchema = {
    ...ARRAY_UI_OPTIONS,
    items: {
      scheme: createVocabularyWidget(schemeVocabulary),
      "custom:grid": includeMaterial
        ? [{ material: 5, scheme: 5, value: 5 }]
        : [{ scheme: 6, value: 10 }],
    },
  };

  if (includeMaterial) {
    baseSchema.items.material = createVocabularyWidget(
      invenioConfig.VOCABULARIES.document.doc_identifiers_materials
    );
  }

  return baseSchema;
};

const createArrayWithTextarea = () => ({
  ...ARRAY_UI_OPTIONS,
  items: TEXTAREA_WIDGET,
});

// Schema configurations
const getAuthorsSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: {
    affiliations: {
      items: {
        identifiers: createIdentifierSchema(
          invenioConfig.VOCABULARIES.document.author.affiliation.identifier
            .scheme
        ),
        "custom:grid": [{ name: 8, identifiers: 8 }],
      },
    },
    alternative_names: ARRAY_UI_OPTIONS,
    identifiers: createIdentifierSchema(
      invenioConfig.VOCABULARIES.document.author.identifier.scheme
    ),
    roles: {
      ...ARRAY_UI_OPTIONS,
      items: createVocabularyWidget(
        invenioConfig.VOCABULARIES.document.author.roles.type
      ),
    },
    type: createVocabularyWidget(
      invenioConfig.VOCABULARIES.document.author.type
    ),
    "custom:grid": [
      { full_name: 6, type: 4, alternative_names: 6 },
      { identifiers: 10, roles: 6 },
      { affiliations: 16 },
    ],
  },
});

const getAlternativeTitlesSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: {
    language: VOCABULARY_SEARCH_LANGUAGE,
    type: createVocabularyWidget(
      invenioConfig.VOCABULARIES.document.alternativeTitle.type
    ),
    "custom:grid": [{ value: 16 }, { language: 10, type: 6 }],
  },
});

const getConferenceInfoSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: {
    country: VOCABULARY_SEARCH_COUNTRY,
    identifiers: createIdentifierSchema(
      invenioConfig.VOCABULARIES.document.conferenceInfo.identifier.scheme
    ),
    "custom:grid": [
      { title: 16 },
      { place: 6, country: 10 },
      { acronym: 6, series: 10 },
      { dates: 10, year: 6 },
      { identifiers: 16 },
    ],
  },
});

const getCopyrightsSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: {
    statement: TEXTAREA_WIDGET,
    "custom:grid": [
      { holder: 6, statement: 10 },
      { url: 8, year: 4, material: 4 },
    ],
  },
});

const getInternalNotesSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: {
    value: TEXTAREA_WIDGET,
    "custom:grid": [{ value: 16 }, { field: 8, user: 8 }],
  },
});

const getLanguagesSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: VOCABULARY_SEARCH_LANGUAGE,
});

const getLicensesSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: {
    internal_notes: TEXTAREA_WIDGET,
    license: {
      id: createVocabularySearchWidget("license", "Type a license..."),
      "custom:grid": [{ id: 16 }],
    },
    "custom:grid": [{ license: 16 }, { material: 6, internal_notes: 10 }],
  },
});

const getPublicationInfoSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: {
    note: TEXTAREA_WIDGET,
    "custom:grid": [
      { journal_title: 6, journal_volume: 5, journal_issue: 5 },
      { artid: 6, pages: 5, year: 5 },
      { note: 16 },
    ],
  },
});

const getSubjectsSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: {
    scheme: createVocabularyWidget(
      invenioConfig.VOCABULARIES.document.doc_subjects
    ),
    "custom:grid": [{ scheme: 6, value: 10 }],
  },
});

const getTagsSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: createVocabularyWidget(invenioConfig.VOCABULARIES.document.tags),
});

const getUrlsSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: {
    "custom:grid": [{ value: 10, description: 6 }],
  },
});

const getKeywordsSchema = () => ({
  ...ARRAY_UI_OPTIONS,
  items: {
    "custom:grid": [{ value: 8, source: 8 }],
  },
});

const getGridLayout = (tooManyAuthors, uiSchemaExtensionsGrid) => {
  const authorsField = tooManyAuthors
    ? { _tooManyAuthorsCustomField: 16 }
    : { authors: 16 };

  return [
    { title: 8, document_type: 4, restricted: 4 },
    { alternative_titles: 8, languages: 8 },
    {
      edition: 3,
      publication_year: 3,
      number_of_pages: 2,
      source: 4,
      curated: 4,
    },
    { "custom:divider": 16 },
    { "custom:divider": 16 },
    authorsField,
    { other_authors: 16 },
    { subjects: 8, tags: 4, keywords: 4 },
    { "custom:divider": 16 },
    { "custom:divider": 16 },
    { imprint: 16 },
    { identifiers: 16 },
    { publication_info: 8, conference_info: 8 },
    { "custom:divider": 16 },
    { "custom:divider": 16 },
    { abstract: 8, alternative_abstracts: 8 },
    { "custom:divider": 16 },
    { physical_description: 8, table_of_content: 8 },
    { "custom:divider": 16 },
    { "custom:divider": 16 },
    { sections: 16 },
    { call_number_prefix: 6, call_number: 6 },
    { copyrights: 8, licenses: 8 },
    { "custom:divider": 16 },
    { urls: 16 },
    { "custom:divider": 16 },
    { internal_notes: 16 },
    { note: 16 },
    ...uiSchemaExtensionsGrid,
  ];
};

export const uiSchema = (title, { tooManyAuthors = false } = {}) => {
  const { uiSchemaExtensions, uiSchemaExtensionsGrid } = getUiSchemaExtensions(
    invenioConfig.DOCUMENTS.extensions
  );

  const baseSchema = {
    ...uiSchemaExtensions,
    abstract: TEXTAREA_WIDGET,
    alternative_abstracts: createArrayWithTextarea(),
    call_number: { "ui:widget": "text" },
    sections: createVocabularyWidget(
      invenioConfig.VOCABULARIES.document.sections,
      true
    ),
    alternative_titles: getAlternativeTitlesSchema(),
    _tooManyAuthorsCustomField: { "ui:disabled": true },
    authors: getAuthorsSchema(),
    conference_info: getConferenceInfoSchema(),
    copyrights: getCopyrightsSchema(),
    curated: BOOLEAN_UI_OPTIONS,
    identifiers: createIdentifierSchema(
      invenioConfig.VOCABULARIES.document.identifier.scheme,
      true
    ),
    cover_metadata: HIDDEN_WIDGET,
    cover_urls: HIDDEN_WIDGET,
    imprint: {
      "custom:grid": [
        { publisher: 8, date: 8 },
        { place: 8, reprint: 8 },
      ],
    },
    internal_notes: getInternalNotesSchema(),
    keywords: getKeywordsSchema(),
    languages: getLanguagesSchema(),
    licenses: getLicensesSchema(),
    note: TEXTAREA_WIDGET,
    restricted: BOOLEAN_UI_OPTIONS,
    other_authors: BOOLEAN_UI_OPTIONS,
    physical_description: TEXTAREA_WIDGET,
    publication_info: getPublicationInfoSchema(),
    subjects: getSubjectsSchema(),
    table_of_content: ARRAY_UI_OPTIONS,
    tags: getTagsSchema(),
    urls: getUrlsSchema(),
    "custom:grid": getGridLayout(tooManyAuthors, uiSchemaExtensionsGrid),
    "custom:root": {
      "custom:formTitle": title,
    },
  };

  return _merge(baseSchema, invenioConfig.DOCUMENTS.editorUiSchema);
};
